import "./styles.sass";

import React from "react";
import leftIcon from "../../assets/icons/arrow-left.svg";
import rightIcon from "../../assets/icons/arrow-right.svg";
import { scrollToTop } from "../../utils";

interface PaginationProps {
  totalItems: number;
  itemsPerPage: number;
  currentPage: number;
  onPageChange: (pageNumber: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  totalItems,
  itemsPerPage,
  currentPage,
  onPageChange,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (pageNumber: number) => {
    if (pageNumber < 1 || pageNumber > totalPages) {
      return;
    }
    onPageChange(pageNumber);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5; // Maximum number of visible page numbers

    if (totalPages <= maxVisiblePages) {
      // If the total number of pages is less than or equal to the maximum visible pages,
      // render all page numbers
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(
          <button
            key={i}
            className={`number ${+currentPage === +i ? "active" : ""}`}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </button>
        );
      }
    } else {
      // If the total number of pages is more than the maximum visible pages,
      // render ellipsis and adjust the visible page numbers based on the current page
      const middlePage = Math.floor(maxVisiblePages / 2);
      let startPage = currentPage - middlePage;
      let endPage = currentPage + middlePage;

      // Adjust startPage and endPage if they go beyond the valid range
      if (startPage < 1) {
        startPage = 1;
        endPage = maxVisiblePages;
      } else if (endPage > totalPages) {
        endPage = totalPages;
        startPage = totalPages - maxVisiblePages + 1;
      }

      // Render the first page only if it's not already included in the visible range
      if (startPage > 1) {
        pageNumbers.push(
          <button
            key={1}
            className={`number ${+currentPage === 1 ? "active" : ""}`}
            onClick={() => handlePageChange(1)}
          >
            1
          </button>
        );

        // Render ellipsis if startPage is greater than 2
        if (startPage > 2) {
          pageNumbers.push(
            <button className="else" key="ellipsis1">
              ...
            </button>
          );
        }
      }

      // Render the visible page numbers
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(
          <button
            key={i}
            className={`number ${+currentPage === i ? "active" : ""}`}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </button>
        );
      }

      // Render ellipsis if endPage is less than totalPages - 1
      if (endPage < totalPages - 1) {
        pageNumbers.push(<li key="ellipsis2">...</li>);
      }

      // Render the last page only if it's not already included in the visible range
      if (endPage < totalPages) {
        pageNumbers.push(
          <button
            key={totalPages}
            className={`number ${+currentPage === +totalPages ? "active" : ""}`}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>
        );
      }
    }

    return pageNumbers;
  };

  return (
    <div className="pagination">
      {totalItems > 1 ? (
        <button
          className="prev"
          onClick={() => handlePageChange(currentPage - 1)}
        >
          <img src={leftIcon} alt="left" />
        </button>
      ) : null}
      <div className="numbers">{renderPageNumbers()}</div>
      {totalItems > 1 ? (
        <button
          className="next"
          onClick={() => handlePageChange(currentPage + 1)}
        >
          <img src={rightIcon} alt="right" />
        </button>
      ) : null}
    </div>
  );
};

export default Pagination;
